import { Dispatch, SetStateAction } from 'react';
import { IEventPauseUpdateParams, IOpenDoorEventCreateProps, IOpenDoorEventProps } from './types';
import { IPaginationParams } from '../../types';
import { api, getToken } from '../../DefaultService';
import logger from '../../../utils/logger';
import { ILprGroupCreateProps, ILprGroupProps } from '../../LprGroupService/types';

export interface WebSocketStreamProps {
  endpoint: string;
  setState: Dispatch<SetStateAction<any[] | any>>;
  pagination?: IPaginationParams;
  onResponse?: () => void;
  setIsLoading?: Dispatch<SetStateAction<boolean>>;
  eventPauseUpdateParams?: IEventPauseUpdateParams;
  isAllByLicense?: boolean;
  filter?: object;
  isBytes?: boolean;
  onSocketError?: (message: string) => void;
}

export const EVENT_ENDPOINT = 'v1/events/opendoor';
export const createOpenDoorEventAsync = async (
  data: IOpenDoorEventCreateProps,
): Promise<IOpenDoorEventProps> => {
  return await api
    .post(EVENT_ENDPOINT, data)
    .then((response) => response.data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.message || 'Erro interno do servidor!',
      );
    });
};

export const getOpenDoorEventStreamByEndpointAsync = async (
  setState: Dispatch<SetStateAction<any[] | any>>,
  pagination?: IPaginationParams,
  onResponse?: () => void,
  setIsLoading?: Dispatch<SetStateAction<boolean>>,
  eventPauseUpdateParams?: IEventPauseUpdateParams,
  isAllByLicense: boolean = true,
  filter?: object,
  isBytes?: boolean,
  onSocketError?: (message: string) => void,
  additionalParams?: string,
  reconnectAttempt: number = 0,
): Promise<void> => {
  try {
    if (setIsLoading) setIsLoading(true);

    if (!getToken()) {
      return;
    }
    let socketUrl =
      // @ts-ignore
      `${window._env_.REACT_APP_API_WS_HOST}/${EVENT_ENDPOINT}${isAllByLicense ? '/getAllByLicense' : ''}/stream?token=Bearer ` +
      window.atob(getToken()!);

    if (additionalParams) socketUrl += `&${additionalParams}`;

    if (pagination) {
      socketUrl += '&pageSize=' + pagination.pageSize;
      socketUrl += '&pageNumber=' + pagination.pageNumber;
    }

    if (filter) {
      socketUrl +=
        '&' +
        Object.entries(filter)
          .flatMap(([key, value]) =>
            Array.isArray(value)
              ? value.map(
                (v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`,
              )
              : `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
          )
          .join('&');
    }

    const socket = new WebSocket(socketUrl);
    if (isBytes) {
      socket.binaryType = 'arraybuffer';
    }

    const reconnect = () => {
      const delay = Math.min(1000 * Math.pow(2, reconnectAttempt), 30000);
      logger.debug(`Tentando reconectar em ${delay / 1000}s...`);
      setTimeout(() => {
        getOpenDoorEventStreamByEndpointAsync(
          setState,
          pagination,
          onResponse,
          setIsLoading,
          eventPauseUpdateParams,
          isAllByLicense,
          filter,
          isBytes,
          onSocketError,
          additionalParams,
          reconnectAttempt + 1,
        );
      }, delay);
    };

    socket.onclose = () => {
      if (setIsLoading) setIsLoading(false);
      reconnect();
    };

    socket.onerror = (error) => {
      if (setIsLoading) setIsLoading(false);
      reconnect();
    };

    socket.onmessage = (event) => {
      reconnectAttempt = 0;
      const data = isBytes ? event.data : JSON.parse(event.data);

      if (data.httpStatusCode && data.httpStatusCode === 404) {
        if (onSocketError) onSocketError(data.userMessage);
        return;
      }

      if (
        eventPauseUpdateParams &&
        pagination &&
        eventPauseUpdateParams.isPaused
      ) {
        const cachedData = eventPauseUpdateParams.getCacheData(pagination);
        if (cachedData) {
          setState(cachedData);
        } else {
          eventPauseUpdateParams.setCacheData(data, pagination);
          setState(data);
        }
      } else {
        setState(data);
      }

      if (setIsLoading) setIsLoading(false);
      if (onResponse) onResponse();
    };
  } catch (error: any) {
    throw new error();
  }
};

export const getAllStreamCountByLicenseAsync = async (
  endpoint: string,
): Promise<number> => {
  return await api
    .get(`${endpoint}/getAllByLicense/count`)
    .then((response) => response.data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.message || 'Erro interno do servidor!',
      );
    });
};

export const getAllEventsCountByDeviceIdAsync = async (
  deviceId: number,
): Promise<number> => {
  return await api
    .get(`${EVENT_ENDPOINT}/${deviceId}/count`)
    .then((response) => response.data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.message || 'Erro interno do servidor!',
      );
    });
};

export const getAllTotalEventAsync = async (
  endpoint: string,
): Promise<number> => {
  return await api
    .get(`${endpoint}`)
    .then((response) => response.data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.message || 'Erro interno do servidor!',
      );
    });
};
export const getAllEventByLicenseAsync = async (
  endpoint: string,
): Promise<number> => {
  return await api
    .get(`${endpoint}/getAllByLicense`)
    .then((response) => response.data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.message || 'Erro interno do servidor!',
      );
    });
};